import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Business as BusinessIcon,
  Assessment as ReportIcon,
  EmojiEvents as TrophyIcon,
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  AttachMoney as MoneyIcon,
  PictureAsPdf as PdfIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Close as CloseIcon,
  BarChart as ChartIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import apiService from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <div className="animate-fadeIn">{children}</div>}
    </div>
  );
}

const CompanyPerformancePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [tabValue, setTabValue] = useState(0);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Single Company Report State
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [companyReport, setCompanyReport] = useState<any>(null);

  // All Companies State
  const [allCompaniesYear, setAllCompaniesYear] = useState(new Date().getFullYear());
  const [allCompaniesMonth, setAllCompaniesMonth] = useState(new Date().getMonth() + 1);
  const [allCompaniesReport, setAllCompaniesReport] = useState<any[]>([]);

  // Performance Comparison State
  const [comparisonYear, setComparisonYear] = useState(new Date().getFullYear());
  const [comparisonMonth, setComparisonMonth] = useState(new Date().getMonth() + 1);
  const [performanceComparison, setPerformanceComparison] = useState<any[]>([]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const data = await apiService.getCompanies();
      setCompanies(data);
    } catch (err) {
      // Failed to fetch companies
    }
  };

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
    setError('');
  };

  // Single Company Report
  const fetchCompanyReport = async () => {
    if (!selectedCompany) {
      setError(t('companyPerformance.errors.selectCompany'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      const data = await apiService.getCompanyMonthlyReport(
        selectedCompany,
        selectedYear,
        selectedMonth
      );
      setCompanyReport(data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('companyPerformance.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const downloadCompanyReportPDF = async () => {
    if (!selectedCompany) {
      setError(t('companyPerformance.errors.selectCompany'));
      return;
    }

    try {
      setLoading(true);
      const blob = await apiService.downloadCompanyMonthlyReportPDF(
        selectedCompany,
        selectedYear,
        selectedMonth
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `company_monthly_report_${selectedCompany}_${selectedYear}_${selectedMonth}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || t('companyPerformance.errors.pdfDownloadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // All Companies Report
  const fetchAllCompaniesReport = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getAllCompaniesMonthly(allCompaniesYear, allCompaniesMonth);
      setAllCompaniesReport(data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('companyPerformance.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Performance Comparison
  const fetchPerformanceComparison = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getCompanyPerformanceComparison(comparisonYear, comparisonMonth);
      setPerformanceComparison(data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('companyPerformance.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const downloadAllCompaniesReportPDF = async () => {
    try {
      setLoading(true);
      const blob = await apiService.downloadAllCompaniesMonthlyPDF(allCompaniesYear, allCompaniesMonth);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `all_companies_monthly_report_${allCompaniesYear}_${allCompaniesMonth}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || t('companyPerformance.errors.pdfDownloadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPerformanceComparisonPDF = async () => {
    try {
      setLoading(true);
      const blob = await apiService.downloadPerformanceComparisonPDF(comparisonYear, comparisonMonth);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `performance_comparison_${comparisonYear}_${comparisonMonth}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || t('companyPerformance.errors.pdfDownloadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const months = [
    { value: 1, label: t('months.january') },
    { value: 2, label: t('months.february') },
    { value: 3, label: t('months.march') },
    { value: 4, label: t('months.april') },
    { value: 5, label: t('months.may') },
    { value: 6, label: t('months.june') },
    { value: 7, label: t('months.july') },
    { value: 8, label: t('months.august') },
    { value: 9, label: t('months.september') },
    { value: 10, label: t('months.october') },
    { value: 11, label: t('months.november') },
    { value: 12, label: t('months.december') },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-green-50 to-emerald-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-[1400px] mx-auto space-y-6">

        {/* Premium Header with Glass Effect - BRAND GREEN */}
        <div className="relative overflow-hidden rounded-3xl">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 opacity-95"></div>
          <div className="absolute inset-0 backdrop-blur-3xl bg-white/10"></div>

          <div className="relative px-6 py-8 md:px-10 md:py-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-start gap-5">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="absolute inset-0 bg-white/30 rounded-2xl blur-xl"></div>
                    <div className="relative bg-white/20 backdrop-blur-md p-4 rounded-2xl border border-white/30 shadow-2xl">
                      <ChartIcon className="text-white" style={{ fontSize: '3rem' }} />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('companyPerformance.title')}
                  </h1>
                  <p className="text-white/90 text-base md:text-lg font-medium max-w-2xl" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('companyPerformance.subtitle')}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-xl border border-white/30">
                  <div className="text-white/80 text-sm font-semibold" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.currentYear')}</div>
                  <div className="text-white text-2xl font-bold">{new Date().getFullYear()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert with Modern Design */}
        {error && (
          <div className="bg-gradient-to-r from-red-50 to-rose-50 border-r-4 border-red-500 rounded-2xl p-5 shadow-lg animate-shake">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-red-100 p-2 rounded-xl">
                  <ErrorIcon className="text-red-600 text-2xl" />
                </div>
                <div>
                  <h4 className="text-red-900 font-bold text-lg mb-1" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.errorOccurred')}</h4>
                  <p className="text-red-700 font-medium" dir={isRTL ? 'rtl' : 'ltr'}>{error}</p>
                </div>
              </div>
              <button
                onClick={() => setError('')}
                className="flex-shrink-0 text-red-400 hover:text-red-600 hover:bg-red-100 p-2 rounded-lg transition-all"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        )}

        {/* Modern Tabs with Glass Morphism - BRAND GREEN THEME */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden">

          {/* Tab Navigation - GREEN THEME */}
          <div className="relative bg-gradient-to-r from-brand-50 to-green-50 border-b border-brand-200/50">
            <div className="grid grid-cols-3 gap-0">
              {[
                { icon: <ReportIcon />, label: t('companyPerformance.tabs.singleReport'), shortLabel: t('companyPerformance.tabs.singleShort'), value: 0 },
                { icon: <BusinessIcon />, label: t('companyPerformance.tabs.allCompanies'), shortLabel: t('companyPerformance.tabs.allShort'), value: 1 },
                { icon: <TrophyIcon />, label: t('companyPerformance.tabs.comparison'), shortLabel: t('companyPerformance.tabs.rankingShort'), value: 2 }
              ].map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => handleTabChange(tab.value)}
                  className={`
                    relative px-4 py-5 md:py-6 font-bold text-sm md:text-base
                    transition-all duration-300 group
                    ${tabValue === tab.value
                      ? 'text-slate-800'
                      : 'text-slate-500 hover:text-slate-700'
                    }
                  `}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className={`
                      p-3 rounded-xl transition-all duration-300
                      ${tabValue === tab.value
                        ? 'bg-gradient-to-br from-brand-600 to-brand-700 text-white shadow-lg scale-110'
                        : 'bg-slate-100 text-slate-400 group-hover:bg-brand-50 group-hover:text-brand-600 group-hover:scale-105'
                      }
                    `}>
                      {React.cloneElement(tab.icon, { className: 'text-2xl' })}
                    </div>
                    <span className="hidden md:block">{tab.label}</span>
                    <span className="md:hidden">{tab.shortLabel}</span>
                  </div>

                  {/* Active Indicator - GREEN */}
                  {tabValue === tab.value && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-600 to-brand-700 rounded-t-full"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content Area */}
          <div className="p-6 md:p-8 lg:p-10">

            {/* Tab 1: Single Company Report */}
            <TabPanel value={tabValue} index={0}>
              <div className="space-y-8">

                {/* Filter Card - GREEN THEME */}
                <div className="bg-gradient-to-br from-brand-50 via-white to-green-50 rounded-2xl p-6 md:p-8 border-2 border-brand-200 shadow-xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-gradient-to-br from-brand-600 to-brand-700 p-3 rounded-xl shadow-lg">
                      <ReportIcon className="text-white text-2xl" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.form.reportSettings')}</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
                    <div className="lg:col-span-5">
                      <label className="block text-sm font-bold text-slate-700 mb-2 mr-1" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('companyPerformance.form.selectCompany')}
                      </label>
                      <select
                        value={selectedCompany}
                        onChange={(e) => setSelectedCompany(e.target.value)}
                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 bg-white
                                 focus:border-brand-500 focus:ring-4 focus:ring-brand-100
                                 transition-all duration-200 font-medium text-slate-700
                                 hover:border-brand-300 shadow-sm"
                        dir={isRTL ? 'rtl' : 'ltr'}
                      >
                        <option value="">{t('companyPerformance.form.selectCompanyPlaceholder')}</option>
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.name || company.companyName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="lg:col-span-3">
                      <label className="block text-sm font-bold text-slate-700 mb-2 mr-1" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('companyPerformance.form.year')}
                      </label>
                      <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 bg-white
                                 focus:border-brand-500 focus:ring-4 focus:ring-brand-100
                                 transition-all duration-200 font-medium text-slate-700
                                 hover:border-brand-300 shadow-sm"
                      >
                        {years.map((year) => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>

                    <div className="lg:col-span-4">
                      <label className="block text-sm font-bold text-slate-700 mb-2 mr-1" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('companyPerformance.form.month')}
                      </label>
                      <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 bg-white
                                 focus:border-brand-500 focus:ring-4 focus:ring-brand-100
                                 transition-all duration-200 font-medium text-slate-700
                                 hover:border-brand-300 shadow-sm"
                        dir="rtl"
                      >
                        {months.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="lg:col-span-6">
                      <label className="block text-sm font-bold text-transparent mb-2">.</label>
                      <button
                        onClick={fetchCompanyReport}
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-brand-600 via-brand-700 to-brand-800
                                 text-white px-6 py-4 rounded-xl font-bold text-base
                                 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-95
                                 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed
                                 disabled:transform-none flex items-center justify-center gap-3
                                 border-b-4 border-brand-900 hover:from-brand-700 hover:via-brand-800"
                      >
                        {loading ? (
                          <>
                            <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>{t('companyPerformance.form.loading')}</span>
                          </>
                        ) : (
                          <>
                            <RefreshIcon className="text-2xl" />
                            <span>{t('companyPerformance.form.viewReport')}</span>
                          </>
                        )}
                      </button>
                    </div>

                    <div className="lg:col-span-6">
                      <label className="block text-sm font-bold text-transparent mb-2">.</label>
                      <button
                        onClick={downloadCompanyReportPDF}
                        disabled={loading || !companyReport}
                        className="w-full bg-gradient-to-r from-red-600 via-red-700 to-rose-700
                                 text-white px-6 py-4 rounded-xl font-bold text-base
                                 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-95
                                 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed
                                 disabled:transform-none flex items-center justify-center gap-3
                                 border-b-4 border-red-800 hover:border-red-900"
                      >
                        <PdfIcon className="text-2xl" />
                        <span>{t('companyPerformance.form.downloadPDF')}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Report Results */}
                {companyReport && (
                  <div className="space-y-6 animate-fade-in-up">

                    {/* Premium Stats Cards - GREEN THEME */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">

                      {/* Card 1: Total Complaints - GREEN */}
                      <div className="group relative overflow-hidden bg-gradient-to-br from-brand-600 via-brand-700 to-emerald-700 rounded-2xl p-6 shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 transition-all duration-300">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full -ml-12 -mb-12 group-hover:scale-150 transition-transform duration-500"></div>

                        <div className="relative">
                          <div className="flex items-center justify-between mb-4">
                            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl shadow-lg">
                              <ReportIcon className="text-white text-3xl" />
                            </div>
                            <TimelineIcon className="text-white/40 text-4xl" />
                          </div>
                          <div className="space-y-1">
                            <p className="text-brand-100 text-sm font-semibold uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.singleReport.stats.totalComplaints')}
                            </p>
                            <h3 className="text-5xl font-black text-white">
                              {companyReport.totalComplaints || 0}
                            </h3>
                          </div>
                        </div>
                      </div>

                      {/* Card 2: Average Resolution Time - EMERALD */}
                      <div className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 rounded-2xl p-6 shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 transition-all duration-300">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full -ml-12 -mb-12 group-hover:scale-150 transition-transform duration-500"></div>

                        <div className="relative">
                          <div className="flex items-center justify-between mb-4">
                            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl shadow-lg">
                              <SpeedIcon className="text-white text-3xl" />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <p className="text-emerald-100 text-sm font-semibold uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.singleReport.stats.avgResolutionTime')}
                            </p>
                            <h3 className="text-5xl font-black text-white">
                              {companyReport.averageResolutionTimeHours || 0}
                              <span className="text-2xl font-bold mr-1">{t('companyPerformance.singleReport.stats.hours')}</span>
                            </h3>
                          </div>
                        </div>
                      </div>

                      {/* Card 3: First Failure - RED (keep as error indicator) */}
                      <div className="group relative overflow-hidden bg-gradient-to-br from-red-500 via-red-600 to-rose-600 rounded-2xl p-6 shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 transition-all duration-300">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full -ml-12 -mb-12 group-hover:scale-150 transition-transform duration-500"></div>

                        <div className="relative">
                          <div className="flex items-center justify-between mb-4">
                            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl shadow-lg">
                              <ErrorIcon className="text-white text-3xl" />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <p className="text-red-100 text-sm font-semibold uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.singleReport.stats.firstFailure')}
                            </p>
                            <h3 className="text-5xl font-black text-white">
                              {companyReport.firstFailureCount || 0}
                            </h3>
                            <p className="text-red-100 text-sm font-medium pt-1" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.singleReport.stats.rate')}:{' '}
                              {companyReport.totalComplaints > 0
                                ? ((companyReport.firstFailureCount / companyReport.totalComplaints) * 100).toFixed(1)
                                : 0}
                              %
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Card 4: SLA Performance - BRIGHT GREEN */}
                      <div className="group relative overflow-hidden bg-gradient-to-br from-green-500 via-green-600 to-emerald-600 rounded-2xl p-6 shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 transition-all duration-300">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full -ml-12 -mb-12 group-hover:scale-150 transition-transform duration-500"></div>

                        <div className="relative">
                          <div className="flex items-center justify-between mb-4">
                            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl shadow-lg">
                              <CheckIcon className="text-white text-3xl" />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <p className="text-green-100 text-sm font-semibold uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.singleReport.stats.slaCompliance')}
                            </p>
                            <h3 className="text-5xl font-black text-white">
                              {companyReport.slaPerformance?.percentage || 0}
                              <span className="text-3xl">%</span>
                            </h3>
                            <p className="text-green-100 text-sm font-medium pt-1" dir={isRTL ? 'rtl' : 'ltr'}>
                              {companyReport.slaPerformance?.onTime || 0} {t('companyPerformance.singleReport.stats.of')}{' '}
                              {companyReport.slaPerformance?.total || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Analysis Section - GREEN THEME */}
                    <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-slate-200">
                      <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-slate-100">
                        <div className="bg-gradient-to-br from-brand-600 to-brand-700 p-3 rounded-xl">
                          <ChartIcon className="text-white text-2xl" />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-800" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.singleReport.analysis.title')}</h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div className="relative group">
                          <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl opacity-10 group-hover:opacity-20 transition-opacity"></div>
                          <div className="relative bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-5 border-2 border-emerald-200 hover:border-emerald-300 transition-all">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="bg-emerald-500 p-2 rounded-lg">
                                <CheckIcon className="text-white text-xl" />
                              </div>
                              <p className="text-sm font-bold text-slate-700" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.singleReport.analysis.resolvedCases')}</p>
                            </div>
                            <h4 className="text-4xl font-black text-emerald-700">
                              {companyReport.completedComplaints || 0}
                            </h4>
                          </div>
                        </div>

                        <div className="relative group">
                          <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-rose-500 rounded-xl opacity-10 group-hover:opacity-20 transition-opacity"></div>
                          <div className="relative bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border-2 border-red-200 hover:border-red-300 transition-all">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="bg-red-500 p-2 rounded-lg">
                                <ErrorIcon className="text-white text-xl" />
                              </div>
                              <p className="text-sm font-bold text-slate-700" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.singleReport.analysis.unresolvedCases')}</p>
                            </div>
                            <h4 className="text-4xl font-black text-red-700">
                              {companyReport.unresolvedCount || 0}
                            </h4>
                          </div>
                        </div>

                        <div className="relative group">
                          <div className="absolute inset-0 bg-gradient-to-br from-brand-400 to-brand-600 rounded-xl opacity-10 group-hover:opacity-20 transition-opacity"></div>
                          <div className="relative bg-gradient-to-br from-brand-50 to-green-50 rounded-xl p-5 border-2 border-brand-200 hover:border-brand-300 transition-all">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="bg-brand-600 p-2 rounded-lg">
                                <SpeedIcon className="text-white text-xl" />
                              </div>
                              <p className="text-sm font-bold text-slate-700" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.singleReport.analysis.avgResolutionTime')}</p>
                            </div>
                            <h4 className="text-4xl font-black text-brand-700">
                              {companyReport.averageResolutionTimeHours || 0}
                              <span className="text-lg font-bold text-slate-500 mr-1">{t('companyPerformance.singleReport.analysis.hours')}</span>
                            </h4>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Deductions Section */}
                    {companyReport.deductions && companyReport.deductions.count > 0 && (
                      <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-slate-200">
                        <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-slate-100">
                          <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-3 rounded-xl">
                            <MoneyIcon className="text-white text-2xl" />
                          </div>
                          <h3 className="text-2xl font-bold text-slate-800" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.singleReport.deductions.title')}</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 border-2 border-amber-200">
                            <p className="text-sm font-bold text-slate-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.singleReport.deductions.count')}</p>
                            <h4 className="text-4xl font-black text-amber-700">
                              {companyReport.deductions.count}
                            </h4>
                          </div>
                          <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border-2 border-red-200">
                            <p className="text-sm font-bold text-slate-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.singleReport.deductions.totalAmount')}</p>
                            <h4 className="text-4xl font-black text-red-700">
                              {companyReport.deductions.total?.toLocaleString() || 0}
                              <span className="text-lg font-bold text-slate-500 mr-1">{t('companyPerformance.singleReport.deductions.currency')}</span>
                            </h4>
                          </div>
                        </div>

                        <div className="overflow-hidden rounded-xl border-2 border-slate-200">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gradient-to-r from-slate-100 to-slate-50">
                                <th className="px-6 py-4 text-right text-sm font-black text-slate-700" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.singleReport.deductions.tableHeaders.amount')}</th>
                                <th className="px-6 py-4 text-right text-sm font-black text-slate-700" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.singleReport.deductions.tableHeaders.reason')}</th>
                                <th className="px-6 py-4 text-right text-sm font-black text-slate-700" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.singleReport.deductions.tableHeaders.date')}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 bg-white">
                              {companyReport.deductions.items?.map((deduction: any, index: number) => (
                                <tr key={index} className="hover:bg-brand-50/30 transition-colors">
                                  <td className="px-6 py-4 text-right">
                                    <span className="inline-flex items-center bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-bold">
                                      {deduction.amount} {t('companyPerformance.singleReport.deductions.currency')}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-right text-slate-700 font-medium">
                                    {deduction.reason}
                                  </td>
                                  <td className="px-6 py-4 text-right text-slate-600">
                                    {new Date(deduction.date).toLocaleDateString('ar-SA')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabPanel>

            {/* Tab 2: All Companies - GREEN THEME */}
            <TabPanel value={tabValue} index={1}>
              <div className="space-y-8">

                {/* Filter Card - GREEN */}
                <div className="bg-gradient-to-br from-brand-50 via-white to-green-50 rounded-2xl p-6 md:p-8 border-2 border-brand-200 shadow-xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-gradient-to-br from-brand-600 to-brand-700 p-3 rounded-xl shadow-lg">
                      <BusinessIcon className="text-white text-2xl" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.form.reportSettings')}</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
                    <div className="lg:col-span-5">
                      <label className="block text-sm font-bold text-slate-700 mb-2 mr-1" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.form.year')}</label>
                      <select
                        value={allCompaniesYear}
                        onChange={(e) => setAllCompaniesYear(parseInt(e.target.value))}
                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 bg-white
                                 focus:border-brand-500 focus:ring-4 focus:ring-brand-100
                                 transition-all duration-200 font-medium text-slate-700
                                 hover:border-brand-300 shadow-sm"
                      >
                        {years.map((year) => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>

                    <div className="lg:col-span-5">
                      <label className="block text-sm font-bold text-slate-700 mb-2 mr-1" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.form.month')}</label>
                      <select
                        value={allCompaniesMonth}
                        onChange={(e) => setAllCompaniesMonth(parseInt(e.target.value))}
                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 bg-white
                                 focus:border-brand-500 focus:ring-4 focus:ring-brand-100
                                 transition-all duration-200 font-medium text-slate-700
                                 hover:border-brand-300 shadow-sm"
                        dir={isRTL ? 'rtl' : 'ltr'}
                      >
                        {months.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="lg:col-span-1">
                      <label className="block text-sm font-bold text-transparent mb-2">.</label>
                      <button
                        onClick={fetchAllCompaniesReport}
                        disabled={loading}
                        className="w-full bg-gradient-to-br from-brand-600 to-brand-700
                                 text-white px-4 py-4 rounded-xl font-bold
                                 shadow-xl hover:shadow-2xl transform hover:scale-105 active:scale-95
                                 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed
                                 disabled:transform-none flex items-center justify-center
                                 border-b-4 border-brand-900"
                      >
                        {loading ? (
                          <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <RefreshIcon className="text-2xl" />
                        )}
                      </button>
                    </div>

                    <div className="lg:col-span-1">
                      <label className="block text-sm font-bold text-transparent mb-2">.</label>
                      <button
                        onClick={downloadAllCompaniesReportPDF}
                        disabled={loading || allCompaniesReport.length === 0}
                        className="w-full bg-gradient-to-br from-red-600 to-red-700
                                 text-white px-4 py-4 rounded-xl font-bold
                                 shadow-xl hover:shadow-2xl transform hover:scale-105 active:scale-95
                                 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed
                                 disabled:transform-none flex items-center justify-center
                                 border-b-4 border-red-800"
                      >
                        <PdfIcon className="text-2xl" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Companies Table - GREEN HEADER */}
                {allCompaniesReport.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-2xl border-2 border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700">
                            <th className="px-6 py-5 text-right text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.allCompanies.tableHeaders.company')}
                            </th>
                            <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.allCompanies.tableHeaders.complaints')}
                            </th>
                            <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide hidden sm:table-cell" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.allCompanies.tableHeaders.resolutionTime')}
                            </th>
                            <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide hidden md:table-cell" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.allCompanies.tableHeaders.firstFailure')}
                            </th>
                            <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.allCompanies.tableHeaders.slaPercentage')}
                            </th>
                            <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide hidden sm:table-cell" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.allCompanies.tableHeaders.deductions')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {allCompaniesReport.map((company, index) => (
                            <tr
                              key={index}
                              className="hover:bg-brand-50/30 transition-all duration-200 group"
                            >
                              <td className="px-6 py-5 text-right">
                                <span className="font-bold text-slate-800 text-base group-hover:text-brand-700 transition-colors" dir={isRTL ? 'rtl' : 'ltr'}>
                                  {company.companyName || t('companyPerformance.allCompanies.unknown')}
                                </span>
                              </td>
                              <td className="px-6 py-5 text-center">
                                <span className="inline-flex items-center justify-center bg-gradient-to-br from-brand-100 to-green-100 text-brand-800 px-4 py-2 rounded-full text-base font-black min-w-[60px] shadow-sm">
                                  {company.totalComplaints || 0}
                                </span>
                              </td>
                              <td className="px-6 py-5 text-center hidden sm:table-cell">
                                <span className="text-slate-700 font-bold">
                                  {company.averageResolutionTimeHours || 0} {t('companyPerformance.allCompanies.tableHeaders.hours')}
                                </span>
                              </td>
                              <td className="px-6 py-5 text-center hidden md:table-cell">
                                <div>
                                  <div className="font-black text-slate-800 text-lg">
                                    {company.firstFailureCount || 0}
                                  </div>
                                  <div className="text-xs text-slate-500 font-semibold mt-1">
                                    (
                                    {company.totalComplaints > 0
                                      ? ((company.firstFailureCount / company.totalComplaints) * 100).toFixed(1)
                                      : 0}
                                    %)
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-5 text-center">
                                <span
                                  className={`inline-flex items-center justify-center px-4 py-2 rounded-full text-sm font-black shadow-sm min-w-[70px] ${
                                    parseFloat(company.slaPerformance?.percentage || '0') >= 90
                                      ? 'bg-gradient-to-br from-emerald-100 to-green-100 text-emerald-800'
                                      : parseFloat(company.slaPerformance?.percentage || '0') >= 70
                                      ? 'bg-gradient-to-br from-amber-100 to-yellow-100 text-amber-800'
                                      : 'bg-gradient-to-br from-red-100 to-rose-100 text-red-800'
                                  }`}
                                >
                                  {company.slaPerformance?.percentage || 0}%
                                </span>
                              </td>
                              <td className="px-6 py-5 text-center hidden sm:table-cell">
                                <span className="text-red-700 font-black text-base">
                                  {company.deductions?.total?.toLocaleString() || 0} {t('companyPerformance.allCompanies.tableHeaders.currency')}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </TabPanel>

            {/* Tab 3: Performance Comparison - GREEN THEME */}
            <TabPanel value={tabValue} index={2}>
              <div className="space-y-8">

                {/* Filter Card - GREEN */}
                <div className="bg-gradient-to-br from-brand-50 via-white to-green-50 rounded-2xl p-6 md:p-8 border-2 border-brand-200 shadow-xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-gradient-to-br from-brand-600 to-brand-700 p-3 rounded-xl shadow-lg">
                      <TrophyIcon className="text-white text-2xl" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.form.comparisonOptions')}</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
                    <div className="lg:col-span-5">
                      <label className="block text-sm font-bold text-slate-700 mb-2 mr-1" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.form.year')}</label>
                      <select
                        value={comparisonYear}
                        onChange={(e) => setComparisonYear(parseInt(e.target.value))}
                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 bg-white
                                 focus:border-brand-500 focus:ring-4 focus:ring-brand-100
                                 transition-all duration-200 font-medium text-slate-700
                                 hover:border-brand-300 shadow-sm"
                      >
                        {years.map((year) => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>

                    <div className="lg:col-span-5">
                      <label className="block text-sm font-bold text-slate-700 mb-2 mr-1" dir={isRTL ? 'rtl' : 'ltr'}>{t('companyPerformance.form.month')}</label>
                      <select
                        value={comparisonMonth}
                        onChange={(e) => setComparisonMonth(parseInt(e.target.value))}
                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 bg-white
                                 focus:border-brand-500 focus:ring-4 focus:ring-brand-100
                                 transition-all duration-200 font-medium text-slate-700
                                 hover:border-brand-300 shadow-sm"
                        dir={isRTL ? 'rtl' : 'ltr'}
                      >
                        {months.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="lg:col-span-1">
                      <label className="block text-sm font-bold text-transparent mb-2">.</label>
                      <button
                        onClick={fetchPerformanceComparison}
                        disabled={loading}
                        className="w-full bg-gradient-to-br from-brand-600 to-brand-700
                                 text-white px-4 py-4 rounded-xl font-bold
                                 shadow-xl hover:shadow-2xl transform hover:scale-105 active:scale-95
                                 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed
                                 disabled:transform-none flex items-center justify-center
                                 border-b-4 border-brand-900"
                      >
                        {loading ? (
                          <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <TrophyIcon className="text-2xl" />
                        )}
                      </button>
                    </div>

                    <div className="lg:col-span-1">
                      <label className="block text-sm font-bold text-transparent mb-2">.</label>
                      <button
                        onClick={downloadPerformanceComparisonPDF}
                        disabled={loading || performanceComparison.length === 0}
                        className="w-full bg-gradient-to-br from-red-600 to-red-700
                                 text-white px-4 py-4 rounded-xl font-bold
                                 shadow-xl hover:shadow-2xl transform hover:scale-105 active:scale-95
                                 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed
                                 disabled:transform-none flex items-center justify-center
                                 border-b-4 border-red-800"
                      >
                        <PdfIcon className="text-2xl" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Ranking Table - GREEN HEADER */}
                {performanceComparison.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-2xl border-2 border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700">
                            <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.comparison.tableHeaders.rank')}
                            </th>
                            <th className="px-6 py-5 text-right text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.comparison.tableHeaders.company')}
                            </th>
                            <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide hidden sm:table-cell" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.comparison.tableHeaders.complaints')}
                            </th>
                            <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.comparison.tableHeaders.slaPercentage')}
                            </th>
                            <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide hidden md:table-cell" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.comparison.tableHeaders.firstFailureRate')}
                            </th>
                            <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide hidden lg:table-cell" dir={isRTL ? 'rtl' : 'ltr'}>
                              {t('companyPerformance.comparison.tableHeaders.resolutionRate')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {performanceComparison.map((company, index) => {
                            const getRankBadge = (rank: number) => {
                              if (rank === 1) return 'bg-gradient-to-br from-yellow-400 via-yellow-500 to-amber-600 text-white shadow-2xl scale-110';
                              if (rank === 2) return 'bg-gradient-to-br from-slate-300 via-gray-400 to-slate-500 text-white shadow-xl scale-105';
                              if (rank === 3) return 'bg-gradient-to-br from-orange-600 via-amber-700 to-orange-800 text-white shadow-lg';
                              return 'bg-slate-200 text-slate-700';
                            };

                            return (
                              <tr
                                key={index}
                                className="hover:bg-brand-50/30 transition-all duration-200 group"
                              >
                                <td className="px-6 py-5 text-center">
                                  <div className="flex justify-center">
                                    <div className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-black text-lg ${getRankBadge(index + 1)}`}>
                                      {index + 1 <= 3 && <TrophyIcon className="text-2xl" />}
                                      <span>#{index + 1}</span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-5 text-right">
                                  <span className="font-bold text-slate-800 text-base group-hover:text-brand-700 transition-colors" dir={isRTL ? 'rtl' : 'ltr'}>
                                    {company.companyName || t('companyPerformance.comparison.unknown')}
                                  </span>
                                </td>
                                <td className="px-6 py-5 text-center hidden sm:table-cell">
                                  <div className="inline-flex items-center justify-center bg-gradient-to-br from-brand-100 to-green-200 px-5 py-3 rounded-xl shadow-sm">
                                    <span className="text-2xl font-black text-brand-800">
                                      {company.totalComplaints || 0}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-5 text-center">
                                  <span
                                    className={`inline-flex items-center justify-center px-4 py-2 rounded-full text-sm font-black shadow-sm min-w-[70px] ${
                                      parseFloat(company.slaPerformance?.percentage || '0') >= 90
                                        ? 'bg-gradient-to-br from-emerald-100 to-green-100 text-emerald-800'
                                        : parseFloat(company.slaPerformance?.percentage || '0') >= 70
                                        ? 'bg-gradient-to-br from-amber-100 to-yellow-100 text-amber-800'
                                        : 'bg-gradient-to-br from-red-100 to-rose-100 text-red-800'
                                    }`}
                                  >
                                    {company.slaPerformance?.percentage || 0}%
                                  </span>
                                </td>
                                <td className="px-6 py-5 text-center hidden md:table-cell">
                                  <span className="inline-flex items-center justify-center bg-red-50 border-2 border-red-200 text-red-700 px-4 py-2 rounded-full text-sm font-black min-w-[70px]">
                                    {company.totalComplaints > 0
                                      ? ((company.firstFailureCount / company.totalComplaints) * 100).toFixed(1)
                                      : 0}
                                    %
                                  </span>
                                </td>
                                <td className="px-6 py-5 text-center hidden lg:table-cell">
                                  <div className="flex items-center justify-center gap-2">
                                    {parseFloat(
                                      company.totalComplaints > 0
                                        ? ((company.completedComplaints / company.totalComplaints) * 100).toFixed(1)
                                        : '0'
                                    ) >= 70 ? (
                                      <TrendingUpIcon className="text-emerald-600 text-2xl" />
                                    ) : (
                                      <TrendingDownIcon className="text-red-600 text-2xl" />
                                    )}
                                    <span
                                      className={`font-black text-lg ${
                                        parseFloat(
                                          company.totalComplaints > 0
                                            ? ((company.completedComplaints / company.totalComplaints) * 100).toFixed(1)
                                            : '0'
                                        ) >= 70
                                          ? 'text-emerald-700'
                                          : 'text-red-700'
                                      }`}
                                    >
                                      {company.totalComplaints > 0
                                        ? ((company.completedComplaints / company.totalComplaints) * 100).toFixed(1)
                                        : 0}
                                      %
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </TabPanel>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyPerformancePage;
